// /client/src/components/TestMMS.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';

const TestMMS = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    phoneNumbers: '',
    message: '',
    mediaUrl: '',
    link: '',
    messageType: 'single',
    podId: '',
    usePodRelay: false
  });
  const [pods, setPods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch pods on mount
  useEffect(() => {
    fetchPods();
  }, []);

  const fetchPods = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/pods?status=active&limit=100', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setPods(data.pods || []);
      }
    } catch (err) {
      console.error('Error fetching pods:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      let response, data;

      // Pod relay mode
      if (formData.usePodRelay) {
        if (!formData.podId) {
          throw new Error('Please select a pod');
        }

        const finalMessage = formData.link 
          ? `${formData.message}\n\n${formData.link}` 
          : formData.message;

        response = await fetch('/api/pod-relay/start', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            podId: formData.podId,
            initialMessage: finalMessage,
            mediaUrl: formData.mediaUrl || null
          })
        });

        data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to start pod relay');
        }

        setSuccess(`✅ Pod relay started! Message sent to ${data.data.memberCount} members`);
      } 
      // Regular messaging mode
      else {
        // Format phone numbers
        const phoneNumbers = formData.phoneNumbers
          .split(',')
          .map(phone => {
            let formatted = phone.trim().replace(/\D/g, '');
            if (formatted.length === 10) formatted = '1' + formatted;
            if (!formatted.startsWith('+')) formatted = '+' + formatted;
            return formatted;
          });

        if (phoneNumbers.length > 10) {
          throw new Error('Maximum 10 phone numbers allowed');
        }

        if (formData.messageType === 'conversation' && phoneNumbers.length < 2) {
          throw new Error('Group conversation requires at least 2 phone numbers');
        }

        response = await fetch('/api/twilio/test-mms', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            phoneNumbers: phoneNumbers,
            message: formData.message,
            mediaUrl: formData.mediaUrl,
            link: formData.link,
            messageType: formData.messageType
          })
        });

        data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to send message');
        }

        setSuccess(`✅ ${data.message}`);
      }
      
      // Clear form
      setFormData({
        phoneNumbers: '',
        message: '',
        mediaUrl: '',
        link: '',
        messageType: 'single',
        podId: '',
        usePodRelay: false
      });

    } catch (err) {
      setError(err.message || 'Failed to send test message');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          
          <Navigation />

          <div className='mt-4 p-0 container'>
            
            <div className="is-flex is-justify-content-center">
              
              <div className="column is-8-tablet is-6-desktop">
                <button 
              className="button is-small is-text mb-4"
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
                <div className="mb-5">
                  <h1 className='title is-3'>Send Test MMS</h1>
                  <p className='subtitle is-6'>
                    Test your Twilio integration by sending a message
                  </p>
                </div>

                {error && (
                  <div className="notification is-danger is-light">
                    <button 
                      className="delete" 
                      onClick={() => setError('')}
                    ></button>
                    {error}
                  </div>
                )}

                {success && (
                  <div className="notification is-success is-light">
                    <button 
                      className="delete" 
                      onClick={() => setSuccess('')}
                    ></button>
                    {success}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="box">
                    
                    {/* Pod Relay Toggle */}
                    <div className="field">
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          name="usePodRelay"
                          checked={formData.usePodRelay}
                          onChange={handleChange}
                        />
                        {' '}Use Pod Relay (group chat with replies)
                      </label>
                      <p className="ml-3 pt-1 small">
                        When enabled, all members see each other's responses
                      </p>
                    </div>

                    {/* Pod Selection */}
                    {formData.usePodRelay && (
                      <div className="field">
                        <label className="label">Select Pod *</label>
                        <div className="control">
                          <div className="select is-fullwidth">
                            <select
                              name="podId"
                              value={formData.podId}
                              onChange={handleChange}
                              required
                            >
                              <option value="">Select a pod...</option>
                              {pods.map(pod => (
                                <option key={pod.id} value={pod.id}>
                                  {pod.podName} ({pod.activeMemberCount || 0} members)
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Phone Numbers (only for non-pod mode) */}
                    {!formData.usePodRelay && (
                      <>
                        <div className="field">
                          <label className="label">Phone Number(s) *</label>
                          <div className="control">
                            <input
                              className="input"
                              type="text"
                              name="phoneNumbers"
                              placeholder="8085008501, 5105551234"
                              value={formData.phoneNumbers}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <p className="ml-3 pt-1 small">
                            Separate multiple numbers with commas (max 10)
                          </p>
                        </div>

                        {/* Message Type Toggle */}
                        {/* <div className="field">
                          <label className="label">Message Type</label>
                          <div className="control">
                            <label className="radio">
                              <input
                                type="radio"
                                name="messageType"
                                value="single"
                                checked={formData.messageType === 'single'}
                                onChange={handleChange}
                              />
                              {' '}Single Messages
                            </label>
                            <label className="radio ml-4">
                              <input
                                type="radio"
                                name="messageType"
                                value="conversation"
                                checked={formData.messageType === 'conversation'}
                                onChange={handleChange}
                              />
                              {' '}Group Conversation
                            </label>
                          </div>
                          <p className="ml-3 pt-1 small">
                            Single: Individual threads. Conversation: Twilio group chat.
                          </p>
                        </div> */}
                      </>
                    )}

                    {/* Message */}
                    <div className="field">
                      <label className="label">Message *</label>
                      <div className="control">
                        <textarea
                          className="textarea"
                          name="message"
                          placeholder="Enter your test message here..."
                          rows="4"
                          value={formData.message}
                          onChange={handleChange}
                          required
                        ></textarea>
                      </div>
                      <p className="ml-3 pt-1 small">
                        {formData.message.length} characters
                      </p>
                    </div>

                    {/* Media URL */}
                    <div className="field">
                      <label className="label">Media URL (Optional)</label>
                      <div className="control">
                        <input
                          className="input"
                          type="url"
                          name="mediaUrl"
                          placeholder="https://example.com/image.jpg"
                          value={formData.mediaUrl}
                          onChange={handleChange}
                        />
                      </div>
                      <p className="ml-3 pt-1 small">
                        Add an image or video URL to send as MMS
                      </p>
                    </div>

                    {/* Link URL */}
                    <div className="field">
                      <label className="label">Link URL (Optional)</label>
                      <div className="control">
                        <input
                          className="input"
                          type="url"
                          name="link"
                          placeholder="https://example.com/article"
                          value={formData.link}
                          onChange={handleChange}
                        />
                      </div>
                      <p className="ml-3 pt-1 small">
                        Add a clickable link (Twilio will shorten it)
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="field is-grouped mt-5">
                      <div className="control">
                        <button 
                          type="submit" 
                          className={`button is-primary ${loading ? 'is-loading' : ''}`}
                          disabled={loading}
                        >
                          {formData.usePodRelay ? 'Start Pod Relay' : 'Send Test Message'}
                        </button>
                      </div>
                      <div className="control">
                        <button 
                          type="button" 
                          className="button is-light"
                          onClick={handleCancel}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>

                  </div>
                </form>

                {/* Info Box */}
                {/* <div className="notification is-info is-light mt-4">
                  <p className="has-text-weight-semibold mb-2">💡 Pod Relay:</p>
                  <ul style={{ marginLeft: '1.5rem' }}>
                    <li>Sends initial message to all pod members</li>
                    <li>When anyone replies, message forwards to all others</li>
                    <li>Each reply prefixed with sender's name</li>
                    <li>Works in separate threads but everyone sees everything</li>
                  </ul>
                </div> */}

              </div>
            </div>
          </div>

        </div>
      </section>
    </>
  );
};

export default TestMMS;